import { Platform } from "react-native";
import { queueLog } from "./batch-logger";

const INTERCEPTED_FLAG = "__memories_console_interceptors_installed__";
const CAPTURING_FLAG = "__memories_console_capturing__";
const ORIGINAL_METHODS_KEY = "__memories_original_console_methods__";

/**
 * Interface holding references to original unintercepted console methods.
 */
interface IOriginalConsoleMethods {
  error: typeof console.error;
  warn: typeof console.warn;
  log: typeof console.log;
  info: typeof console.info;
  debug: typeof console.debug;
}

/**
 * Gets the global execution context across Node, React Native, and Web environments.
 * @returns {Record<string, any>} The global object
 */
function getGlobalScope(): Record<string, any> {
  if (typeof globalThis !== "undefined") {
    return globalThis as Record<string, any>;
  }
  if (typeof window !== "undefined") {
    return window as Record<string, any>;
  }
  return (global || {}) as Record<string, any>;
}

/**
 * Checks whether logging capture is currently active to prevent infinite recursion.
 * @returns {boolean} True if capture is in progress
 */
function isCapturing(): boolean {
  const g = getGlobalScope();
  return Boolean(g[CAPTURING_FLAG]);
}

/**
 * Sets the global reentrancy capture flag.
 * @param {boolean} value - New capture flag state
 * @returns {void}
 */
function setCapturing(value: boolean): void {
  const g = getGlobalScope();
  g[CAPTURING_FLAG] = value;
}

/**
 * Safely stringifies and joins arguments passed to console logging.
 * Handles Error objects, BigInt, Symbols, circular structures, and throwing getters gracefully.
 * @param {unknown[]} args - Array of console arguments
 * @returns {string} Joined string description of args
 */
export function formatArgs(args: unknown[]): string {
  if (!Array.isArray(args) || args.length === 0) {
    return "";
  }

  return args
    .map((arg) => {
      if (arg === null) {
        return "null";
      }
      if (arg === undefined) {
        return "undefined";
      }
      if (arg instanceof Error) {
        return arg.stack || `${arg.name}: ${arg.message}`;
      }
      if (typeof arg === "symbol") {
        return arg.toString();
      }
      if (typeof arg === "bigint") {
        return `${arg.toString()}n`;
      }
      if (typeof arg === "function") {
        return `[Function: ${arg.name || "anonymous"}]`;
      }
      if (typeof arg === "object") {
        try {
          const serialized = JSON.stringify(arg, (_key, value) => {
            if (typeof value === "bigint") {
              return value.toString();
            }
            if (typeof value === "symbol") {
              return value.toString();
            }
            if (typeof value === "function") {
              return `[Function: ${value.name || "anonymous"}]`;
            }
            if (value instanceof Error) {
              return {
                name: value.name,
                message: value.message,
                stack: value.stack,
              };
            }
            // Truncate individual huge string fields (e.g. embedded base64 data URLs)
            if (typeof value === "string" && value.length > 2000) {
              return value.slice(0, 2000) + "... [truncated]";
            }
            return value;
          });
          if (serialized.length > 20000) {
            return serialized.slice(0, 20000) + "... [truncated object]";
          }
          return serialized;
        } catch {
          try {
            return Object.prototype.toString.call(arg);
          } catch {
            return "[Unserializable Object]";
          }
        }
      }
      try {
        return String(arg);
      } catch {
        return "[Unprintable Value]";
      }
    })
    .join(" ");
}

/**
 * Safely extracts an Error instance from console arguments without throwing
 * even if proxies or custom hasInstance hooks fail.
 * @param {unknown[]} args - Console arguments
 * @returns {Error | undefined} Found Error or undefined
 */
export function safeFindError(args: unknown[]): Error | undefined {
  if (!Array.isArray(args)) {
    return undefined;
  }
  for (const arg of args) {
    try {
      if (arg instanceof Error) {
        return arg;
      }
    } catch {
      // Ignore proxy or Symbol.hasInstance inspection errors
    }
  }
  return undefined;
}

/**
 * Safely extracts stack trace string from an error argument.
 * @param {Error | undefined} error - Error argument
 * @returns {string | undefined} Stack string if valid
 */
export function safeGetErrorStack(error?: Error): string | undefined {
  if (!error) {
    return undefined;
  }
  try {
    if (typeof error.stack === "string") {
      return error.stack;
    }
    return `${error.name}: ${error.message}`;
  } catch {
    return undefined;
  }
}

/**
 * Known benign framework-level deprecation and development warnings that should not
 * pollute cloud logging telemetry.
 */
const IGNORED_WARNING_PATTERNS: string[] = [
  "Cannot connect to Expo CLI",
  "props.pointerEvents is deprecated",
  "flexWrap: `wrap` is not supported with the `VirtualizedList`",
  "style.tintColor is deprecated",
  "Messaging WebSocket error",
  "Error parsing WebSocket messaging frame",
  "Could not initiate messaging WebSocket",
  "[BatchLogger]",
  "shadow* style props are deprecated",
  "Use \"boxShadow\"",
  "[Reanimated]",
  "Setting a timer for a long period of time",
];

/**
 * Checks whether a warning message matches known benign framework deprecation patterns.
 * @param {string} message - Warning text to test.
 * @returns {boolean} True if the message should be ignored from remote batch logging.
 */
export function shouldIgnoreWarning(message: string): boolean {
  if (message.includes("ExpoShareIntentModule not found")) {
    return Platform.OS === "web";
  }
  return IGNORED_WARNING_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Intercepts console.error, console.warn, console.log, console.info, and console.debug to forward
 * their outputs to the local batch logging queue.
 * Idempotent: safe to invoke repeatedly across hot reloads or multiple imports.
 * @returns {void}
 */
export function initConsoleInterceptors(): void {
  const g = getGlobalScope();
  if (g[INTERCEPTED_FLAG] || (console as any)[INTERCEPTED_FLAG]) {
    return;
  }

  const originals: IOriginalConsoleMethods = {
    error: console.error,
    warn: console.warn,
    log: console.log,
    info: console.info,
    debug: console.debug,
  };

  g[INTERCEPTED_FLAG] = true;
  (console as any)[INTERCEPTED_FLAG] = true;
  (console as any)[ORIGINAL_METHODS_KEY] = originals;

  console.error = (...args: any[]): void => {
    try {
      Function.prototype.apply.call(originals.error, console, args);
    } catch {
      // Benign fallback if original console.error throws
    }

    if (isCapturing()) {
      return;
    }

    setCapturing(true);
    try {
      const message = formatArgs(args);
      const errorArg = safeFindError(args);
      const stack = safeGetErrorStack(errorArg);
      queueLog("error", message, stack);
    } catch {
      // Telemetry failure must never crash the app or pollute console
    } finally {
      setCapturing(false);
    }
  };

  console.warn = (...args: any[]): void => {
    try {
      Function.prototype.apply.call(originals.warn, console, args);
    } catch {
      // Benign fallback
    }

    if (isCapturing()) {
      return;
    }

    setCapturing(true);
    try {
      const message = formatArgs(args);
      if (!shouldIgnoreWarning(message)) {
        queueLog("warn", message);
      }
    } catch {
      // Telemetry failure must never crash the app
    } finally {
      setCapturing(false);
    }
  };

  console.log = (...args: any[]): void => {
    try {
      Function.prototype.apply.call(originals.log, console, args);
    } catch {
      // Benign fallback
    }

    if (isCapturing()) {
      return;
    }

    setCapturing(true);
    try {
      const message = formatArgs(args);
      queueLog("log", message);
    } catch {
      // Telemetry failure must never crash the app
    } finally {
      setCapturing(false);
    }
  };

  console.info = (...args: any[]): void => {
    try {
      Function.prototype.apply.call(originals.info, console, args);
    } catch {
      // Benign fallback
    }

    if (isCapturing()) {
      return;
    }

    setCapturing(true);
    try {
      const message = formatArgs(args);
      queueLog("info", message);
    } catch {
      // Telemetry failure must never crash the app
    } finally {
      setCapturing(false);
    }
  };

  console.debug = (...args: any[]): void => {
    try {
      Function.prototype.apply.call(originals.debug, console, args);
    } catch {
      // Benign fallback
    }

    if (isCapturing()) {
      return;
    }

    setCapturing(true);
    try {
      const message = formatArgs(args);
      queueLog("info", message);
    } catch {
      // Telemetry failure must never crash the app
    } finally {
      setCapturing(false);
    }
  };
}

/**
 * Restores original unintercepted console methods. Used primarily in test isolation.
 * @returns {void}
 */
export function uninstallConsoleInterceptors(): void {
  const g = getGlobalScope();
  const originals: IOriginalConsoleMethods | undefined = (console as any)[ORIGINAL_METHODS_KEY];
  if (originals) {
    console.error = originals.error;
    console.warn = originals.warn;
    console.log = originals.log;
    console.info = originals.info;
    console.debug = originals.debug;
    delete (console as any)[ORIGINAL_METHODS_KEY];
  }
  delete (console as any)[INTERCEPTED_FLAG];
  delete g[INTERCEPTED_FLAG];
  delete g[CAPTURING_FLAG];
}

initConsoleInterceptors();
